import { useState, useMemo, useCallback, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import {Map as MapGl, NavigationControl} from "react-map-gl/mapbox";
import { ErrorBoundary } from '@sentry/react';
import { point, featureCollection } from '@turf/turf';
import { useTranslation } from 'react-i18next';
import { mapFitFeatures } from 'geojson-map-fit-mercator'
import { DateTime } from 'luxon';

import { getSiteLastData, getSiteState } from '../helpers/SiteData.js';
import DecimalValueFormat from '../components/DecimalValueFormat.jsx';
import SiteMapMarker from '../components/SiteMapMarker.jsx';

function defaultMapSize(mapDimensions) {
  return [mapDimensions?.width, mapDimensions?.height];
}

function RiverSiteMapMarker ({site, allData, goToSite, expandedMarkers, mapZoom}) {
  const { t } = useTranslation();
  const lastSiteData = getSiteLastData(allData, site.id, site.types[0]);
  const siteState = getSiteState(allData, site.id);

  const mapMarkerExpandedDefault = expandedMarkers ? site.properties?.mapMarker?.expanded : false;
  const [expanded, setExpandedState] = useState(mapMarkerExpandedDefault);
  // ExpandedTime is used for debouncing the click event.
  const [expandedTime, setExpandedTime] = useState(0);

  function setExpanded(newExpandedState) {
    if(newExpandedState && !expanded) {
      setExpandedTime(DateTime.now());
    }
    setExpandedState(newExpandedState);
  }

  let siteValue = lastSiteData?.value ? `${DecimalValueFormat({ value: lastSiteData?.value })} ${t(`units.${site.properties.unit}.label_short`)}` : undefined;
  if(siteState?.siteState === 'no_data') {
    siteValue = t('status.no_data.label');
  }

  return (<SiteMapMarker
    title={site.name}
    value={siteValue}
    longitude={site.coords[0]}
    latitude={site.coords[1]}
    status={siteState?.siteState}
    onClick={() => expanded == true && expandedTime < DateTime.now().minus({milliseconds: 200}) ? goToSite(site.id) : setExpanded(true)}
    onBlur={() => setExpanded(mapMarkerExpandedDefault)}
    onMouseEnter={() => setExpanded(true)}
    onMouseLeave={() => setExpanded(mapMarkerExpandedDefault)}
    anchor={site.properties?.mapMarker?.anchor}
    expanded={expanded}
    blip={site.properties?.mapMarker?.minZoom > mapZoom}
  />);
}

function RiverSiteMap({
  geometry,
  sites,
  allData,
  padding,
  className,
  goToSite,
  style,
  mapSize = defaultMapSize,
  interactive = false,
  expandedMarkers = true,
}) {
  const { t } = useTranslation();
  const [mapWrapperRef, mapDimensions] = useMeasure();
  const [lastMapSize, setLastMapSize] = useState([0, 0]);

  const [map, setMap] = useState(0);
  const [mapZoom, setMapZoom] = useState(0);

  const mapRef = useCallback((node) => {
    if (typeof node?.getMap === 'function') {
      setMap(node.getMap());
    }
  }, []);

  const mapSites = useMemo(() => (sites || []).filter((site) => !!site.properties?.mapMarker), [sites]);
  const siteFeatures = useMemo(() => {
    let features = (mapSites || []).map((site) => point(site.coords))
    if (geometry?.features?.length > 0) { features = features.concat(geometry.features) }
    return featureCollection(features)
  }, [mapSites, geometry]);

  useEffect(() => {
    const [mapWidthEvaluated, mapHeightEvaluated] = typeof mapSize === 'function' ? mapSize(mapDimensions) : mapSize;
    if (lastMapSize[0] === mapWidthEvaluated && lastMapSize[1] === mapHeightEvaluated ||
      mapWidthEvaluated === 0 || mapHeightEvaluated === 0 || !map) {
      return;
    }
    setLastMapSize([mapWidthEvaluated, mapHeightEvaluated]);

    let { bearing, zoom, center } = mapFitFeatures(
      siteFeatures,
      [mapWidthEvaluated, mapHeightEvaluated],
      {
        padding: padding,
        preferredBearing: -10,
      }
    );

    map.easeTo({
      zoom: zoom,
      center: center,
      bearing: bearing,
    })
  }, [mapDimensions, map, mapSize, lastMapSize, siteFeatures, setLastMapSize, padding]);

  let bearing = 0;
  let zoom = 1;
  let center = [0, 0];

  return (
    <ErrorBoundary
      fallback={({error}) => {
        console.error(error);
        return (<div>Map failed to load...</div>)
      }}
    >
      {(sites && (
        <div ref={mapWrapperRef} style={style} className={className}>
          <MapGl
            ref={mapRef}
            mapboxAccessToken="pk.eyJ1IjoidGpkIiwiYSI6ImNtOGhpZm4wcTAwb3AycnB1YmJiYmQwbDgifQ.djxW1otZnd1fCnE6bAYwpQ"
            projection="mercator"
            initialViewState={{
             zoom: zoom,
             longitude: center[0],
             latitude: center[1],
             bearing: bearing,
            }}
            style={{width: '100%', height: '100%'}}
            cursor={'default'}
            boxZoom={interactive}
            doubleClickZoom={interactive}
            scrollZoom={interactive}
            dragPan={interactive}
            dragRotate={interactive}
            touchPitch={interactive}
            touchZoomRotate={interactive}
            keyboard={interactive}
            logoPosition="bottom-right"
            minZoom={9}
            maxZoom={15}
            attributionControl={true}
            mapStyle="mapbox://styles/tjd/cm702hdql00im01re3a586t37"
            onZoom={(e) => setMapZoom(e.viewState.zoom)}
          >
            {mapSites.map(site => (
              <RiverSiteMapMarker
                key={site.id}
                site={site}
                allData={allData}
                goToSite={goToSite}
                expandedMarkers={expandedMarkers}
                mapZoom={mapZoom}
              />
            ))}
            {interactive && (<NavigationControl position="bottom-left"/>)}
          </MapGl>
        </div>
      ))}
    </ErrorBoundary>
  );
}

export default RiverSiteMap;
